import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Download, Check, Loader2, Clock } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { useTheme } from '@/context/ThemeContext';

import { API } from '@/lib/apiBase';
const EAGER_LOAD_COUNT = 8;
// Fallback used only if ThemeContext hasn't loaded yet — should never be
// visible to the user because ThemeContext blocks first render.
const PHOTO_RETENTION_DAYS_FALLBACK = 60;
const URGENCY_THRESHOLD_DAYS = 14;

// Days remaining before this photo is auto-deleted from the gallery.
// Returns null if no created_at is available.
const daysLeftFor = (photo, retentionDays) => {
  if (!photo?.created_at) return null;
  const created = new Date(photo.created_at);
  if (Number.isNaN(created.getTime())) return null;
  const ageMs = Date.now() - created.getTime();
  const daysLeft = retentionDays - Math.floor(ageMs / (1000 * 60 * 60 * 24));
  return Math.max(0, daysLeft);
};

export const PhotoCard = ({
  photo, isClassPhoto = false, index = 0,
  isInCart, canDownloadFree, downloading,
  onSelect, onToggleCart, onDownload
}) => {
  const { t, language } = useLanguage();
  const { settings } = useTheme();
  // Live from site_settings — bumping `photo_retention_days` in Admin updates
  // every banner without a redeploy.
  const retentionDays = Number(settings?.photo_retention_days) > 0
    ? Number(settings.photo_retention_days)
    : PHOTO_RETENTION_DAYS_FALLBACK;
  const inCart = isInCart(photo.photo_id);
  const free = canDownloadFree(photo);
  const daysLeft = daysLeftFor(photo, retentionDays);
  const urgent = daysLeft !== null && daysLeft <= URGENCY_THRESHOLD_DAYS;

  return (
    <Card className="bg-card border-border overflow-hidden card-hover group">
      <div
        className={`${isClassPhoto ? 'aspect-[4/3]' : 'aspect-square'} relative cursor-pointer`}
        onClick={() => onSelect(photo)}
      >
        <img
          src={`${API}/photos/${photo.photo_id}/preview?size=thumb`}
          alt={isClassPhoto ? t('gallery.classPhoto') : t('common.photoAlt')}
          loading={index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'}
          fetchPriority={index < 2 ? 'high' : 'auto'}
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {isClassPhoto && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-primary text-white text-xs font-bold rounded-sm uppercase">
            {t('gallery.classPhoto')}
          </div>
        )}
        {urgent && (
          <div
            className="absolute top-3 right-3 px-2.5 py-1 bg-red-600 text-white text-[11px] font-bold rounded-sm flex items-center gap-1 shadow-lg"
            data-testid="urgency-badge"
          >
            <Clock className="w-3 h-3" />
            {daysLeft === 0
              ? t('gallery.lastDay')
              : t('gallery.daysLeft').replace('{n}', daysLeft)}
          </div>
        )}
      </div>

      <CardContent className={isClassPhoto ? 'p-4' : 'p-3'}>
        <div className="flex items-center justify-between">
          <div className={`font-mono ${isClassPhoto ? 'text-lg' : 'text-sm'} font-bold ${free ? 'text-green-500' : 'text-primary'}`}>
            {free ? t('gallery.free') : `CHF ${photo.price?.toFixed(2)}`}
          </div>

          <div className="flex gap-1">
            {free ? (
              <Button
                size="sm"
                variant={isClassPhoto ? 'default' : 'ghost'}
                onClick={(e) => { e.stopPropagation(); onDownload(photo); }}
                disabled={downloading === photo.photo_id}
                className={isClassPhoto ? 'bg-green-600 hover:bg-green-700' : 'h-8 px-2'}
                data-testid={`download-free-${photo.photo_id}`}
              >
                {downloading === photo.photo_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1" />
                    {isClassPhoto && t('gallery.downloadFree')}
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant={inCart ? 'secondary' : isClassPhoto ? 'default' : 'ghost'}
                onClick={(e) => { e.stopPropagation(); onToggleCart(photo); }}
                className={inCart ? '' : isClassPhoto ? 'bg-primary hover:bg-primary/90' : 'h-8 px-2'}
                data-testid={`add-cart-${photo.photo_id}`}
              >
                {inCart ? (
                  <>
                    <Check className={`w-4 h-4 ${isClassPhoto ? 'mr-1' : 'text-green-500'}`} />
                    {isClassPhoto && t('gallery.inCart')}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    {isClassPhoto && t('gallery.addToCart')}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
